import { NoFacilityScreen } from "@/components/common/NoFacilityScreen";
import { translateAlert } from "@/utils/config-utils";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Button,
  Calendar,
  CheckCircle,
  DollarSign,
  Settings,
  UserCheck,
  Users,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActivityCard } from "../../components/molecules/ActivityCard";
import { DoctorList } from "../../components/molecules/DoctorList";
import { QuickActionCard } from "../../components/molecules/QuickActionCard";
import { StatCard } from "../../components/molecules/StatCard";
import useFacilityStats from "../../hooks/useFacilityStats";
import useFacilityUser from "../../hooks/useFacilityUser";
import { fetchDashboardDataRequest } from "../../store/reducers/dashboardSlice";
import { RootState } from "../../store/store";
import { Doctor } from "../../types/doctor.definition";
import { describeActivity, translateTime } from "../../utils/i18n-utils";

export const ManagerDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const facilityUser = useFacilityUser();
  const { stats: facilityStats, loading, error: statsError } =
    useFacilityStats();
  const storedFacilityId = useSelector(
    (state: RootState) => state.dashboard.facilityId
  );
  const userRole = useSelector((state: RootState) =>
    state.session.session?.user?.roleName?.toLowerCase()
  );

  // Get dashboard data from Redux store
  const workingDoctors = useSelector(
    (state: RootState) => state.dashboard.workingDoctors
  );
  const workingDoctorsLoading = useSelector(
    (state: RootState) => state.dashboard.workingDoctorsLoading
  );
  const todaySchedule = useSelector(
    (state: RootState) => state.dashboard.todaySchedule
  );
  const scheduleLoading = useSelector(
    (state: RootState) => state.dashboard.scheduleLoading
  );
  const recentActivity = useSelector(
    (state: RootState) => state.dashboard.recentActivity
  );
  const activityLoading = useSelector(
    (state: RootState) => state.dashboard.activityLoading
  );
  const alerts = useSelector((state: RootState) => state.dashboard.alerts);
  const alertsLoading = useSelector(
    (state: RootState) => state.dashboard.alertsLoading
  );

  const [animateCards, setAnimateCards] = useState(false);

  const hasFacility = !!facilityUser.healthcareFacility?.id;

  // Fetch all dashboard data when facility ID is available
  useEffect(() => {
    const facilityId = facilityUser.healthcareFacility?.id;
    if (!facilityId) return;

    // Fetch once per facility. The request records the facility id (even if
    // the fetch fails), so a failure does not re-trigger the effect in a loop;
    // the user retries explicitly (MED-1648).
    const shouldFetch =
      !loading &&
      (storedFacilityId == null ||
        String(storedFacilityId) !== String(facilityId));

    if (shouldFetch) {
      // Fetch all dashboard data at once
      dispatch(fetchDashboardDataRequest({ facilityId }));
    }
  }, [dispatch, facilityUser.healthcareFacility?.id, loading, storedFacilityId]);

  // Trigger animations after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Filter quick actions based on user role (must be before any early returns)
  const quickActions = useMemo(() => {
    const allActions = [
      {
        title: t("dashboard.quickActions.manageAppointments.title"),
        description: t("dashboard.quickActions.manageAppointments.description"),
        icon: Calendar,
        path: "/appointments",
        key: "manageAppointments",
      },
      {
        title: t("dashboard.quickActions.staffManagement.title"),
        description: t("dashboard.quickActions.staffManagement.description"),
        icon: Users,
        path: "/manager/staff",
        key: "staffManagement",
      },
      {
        title: t("dashboard.quickActions.viewReports.title"),
        description: t("dashboard.quickActions.viewReports.description"),
        icon: BarChart3,
        path: "/manager/reports",
        key: "viewReports",
      },
      {
        title: t("dashboard.quickActions.facilitySettings.title"),
        description: t("dashboard.quickActions.facilitySettings.description"),
        icon: Settings,
        path: "/manager/settings",
        key: "facilitySettings",
      },
    ];

    // If user is Secretary, filter out restricted actions
    if (userRole === "secretary") {
      const restrictedActions = [
        "manageAppointments",
        "staffManagement",
        "viewReports",
        "facilitySettings",
      ];
      return allActions.filter(
        (action) => !restrictedActions.includes(action.key)
      );
    }

    // For manager or any other role, show all actions
    return allActions;
  }, [t, userRole]);

  // No facility assigned — show dedicated screen
  if (!hasFacility) {
    return <NoFacilityScreen />;
  }

  // Stats failed to load: say so and let the user retry
  if (!facilityStats && statsError && !loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="space-y-3 text-center">
          <p className="text-neutral-600">{t("dashboard.statsError")}</p>
          <Button
            variant="outline"
            onClick={() =>
              dispatch(
                fetchDashboardDataRequest({
                  facilityId: facilityUser.healthcareFacility!.id,
                })
              )
            }
          >
            {t("dashboard.statsRetry")}
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state while stats are being fetched
  if (!facilityStats) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-neutral-600">{t("dashboard.loadingStats")}</p>
        </div>
      </div>
    );
  }

  const isSecretary = userRole === "secretary";

  // Transform facility stats for display. Each tile links to its natural page;
  // a tile whose page the role cannot open is not a link (MED-1648).
  const stats = [
    {
      title: t("dashboard.stats.totalPatients"),
      to: "/patients",
      value: facilityStats.patientStatistics.total.toLocaleString(),
      icon: Users,
      change: {
        value: `${facilityStats.patientStatistics.monthChange > 0 ? "+" : ""}${facilityStats.patientStatistics.monthChange
          }%`,
        type:
          facilityStats.patientStatistics.monthChange >= 0
            ? ("increase" as const)
            : ("decrease" as const),
      },
      description: t("dashboard.stats.fromLastMonth"),
    },
    {
      title: t("dashboard.stats.todaysAppointments"),
      to: "/appointments",
      // MED-1646/1647: today's count; `total` is the all-time count
      value: (
        facilityStats.appointmentStatistics.todayTotal ?? 0
      ).toLocaleString(),
      icon: Calendar,
      change: {
        value: t("dashboard.stats.onSchedule"),
        type: "neutral" as const,
      },
      description: t("dashboard.stats.appointmentsTodayDescription"),
    },
    {
      title: t("dashboard.stats.doctors"),
      to: isSecretary ? "/secretary/availability" : "/manager/staff",
      value: facilityStats.doctorStatistics.total.toLocaleString(),
      icon: UserCheck,
      change: {
        value: `${facilityStats.doctorStatistics.monthChange > 0 ? "+" : ""}${facilityStats.doctorStatistics.monthChange
          }%`,
        type:
          facilityStats.doctorStatistics.monthChange >= 0
            ? ("increase" as const)
            : ("decrease" as const),
      },
      description: t("dashboard.stats.activeDoctors"),
    },
    {
      title: t("dashboard.stats.monthlyAppointments"),
      to: isSecretary ? undefined : "/manager/reports",
      value: facilityStats.appointmentStatistics.lastMonth.toLocaleString(),
      icon: DollarSign,
      change: {
        value: `${facilityStats.appointmentStatistics.monthChange > 0 ? "+" : ""
          }${facilityStats.appointmentStatistics.monthChange}%`,
        type:
          facilityStats.appointmentStatistics.monthChange >= 0
            ? ("increase" as const)
            : ("decrease" as const),
      },
      description: t("dashboard.stats.fromLastMonth"),
    },
  ];

  // Helper function to get icon for activity type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case "appointment":
        return Calendar;
      case "checkin":
        return CheckCircle;
      case "completion":
        return CheckCircle;
      case "cancellation":
        return AlertCircle;
      default:
        return Activity;
    }
  };

  // Helper function to get icon color for activity type
  const getActivityIconColor = (type: string) => {
    switch (type) {
      case "appointment":
        return "text-primary-600";
      case "checkin":
        return "text-success-600";
      case "completion":
        return "text-success-600";
      case "cancellation":
        return "text-warning-600";
      default:
        return "text-secondary-600";
    }
  };

  // Helper function to get icon background color for activity type
  const getActivityIconBgColor = (type: string) => {
    switch (type) {
      case "appointment":
        return "bg-ews-primary-100";
      case "checkin":
        return "bg-success-100";
      case "completion":
        return "bg-success-100";
      case "cancellation":
        return "bg-warning-100";
      default:
        return "bg-ews-secondary-100";
    }
  };

  // Transform recent activity from Redux to ActivityCard format with translations
  // Sort by time (most recent first) and limit to 5 items
  const sortedActivities = [...recentActivity]
    .sort((a, b) => {
      // Try to parse time strings as dates
      // Handle both ISO date strings and formatted time strings
      const parseTime = (timeStr: string): number => {
        // Try parsing as ISO date first
        const date = new Date(timeStr);
        if (!isNaN(date.getTime())) {
          return date.getTime();
        }
        // If parsing fails, return 0 to maintain original order
        return 0;
      };

      const timeA = parseTime(a.time);
      const timeB = parseTime(b.time);

      // Sort descending (most recent first)
      // If both times are valid, sort by time
      // If one is invalid, keep original order
      if (timeA > 0 && timeB > 0) {
        return timeB - timeA;
      }
      // Maintain original order if dates can't be parsed
      return 0;
    })
    .slice(0, 5);

  const activities = sortedActivities.map((activity) => {
    // Translate activity titles based on type
    let translatedTitle = activity.title;

    // Map activity types to translation keys
    const titleTranslationMap: Record<string, string> = {
      "New appointment scheduled":
        "dashboard.recentActivity.types.newAppointment",
      "Appointment updated":
        "dashboard.recentActivity.types.appointmentUpdated",
      "Appointment completed":
        "dashboard.recentActivity.types.appointmentCompleted",
      "Appointment canceled":
        "dashboard.recentActivity.types.appointmentCanceled",
      "Patient check-in": "dashboard.recentActivity.types.patientCheckIn",
    };

    // Try to find exact match first
    if (titleTranslationMap[activity.title]) {
      translatedTitle = t(titleTranslationMap[activity.title]);
    } else {
      // Fallback: translate based on activity type
      switch (activity.type) {
        case "appointment":
          translatedTitle = t("dashboard.recentActivity.types.newAppointment");
          break;
        case "completion":
          translatedTitle = t(
            "dashboard.recentActivity.types.appointmentCompleted"
          );
          break;
        case "cancellation":
          translatedTitle = t(
            "dashboard.recentActivity.types.appointmentCanceled"
          );
          break;
        case "checkin":
          translatedTitle = t("dashboard.recentActivity.types.patientCheckIn");
          break;
        default:
          translatedTitle = activity.title; // Keep original if no match
      }
    }

    // Translate time strings
    const translatedTime = translateTime(activity.time, t);

    return {
      title: translatedTitle,
      description: describeActivity(activity, t),
      time: translatedTime,
      icon: getActivityIcon(activity.type),
      iconColor: getActivityIconColor(activity.type),
      iconBgColor: getActivityIconBgColor(activity.type),
    };
  });

  return (
    <div className="p-4 space-y-4">
      {/* Page Header */}
      <div
        className={`mb-6 transition-opacity duration-300 ${animateCards ? "opacity-100" : "opacity-0"
          }`}
      >
        <h1 className="mb-2 text-2xl font-bold text-neutral-900">
          {t("dashboard.title")}
        </h1>
        <p className="text-neutral-600">{t("dashboard.welcomeMessage")}</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`transition-opacity duration-300 ease-out ${animateCards ? "opacity-100" : "opacity-0"
              }`}
            style={{
              transitionDelay: `${index * 40}ms`,
            }}
          >
            <StatCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              change={stat.change}
              description={stat.description}
              to={stat.to}
            />
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        {quickActions.map((action, index) => (
          <div
            key={index}
            className={`transition-opacity duration-300 ease-out ${animateCards ? "opacity-100" : "opacity-0"
              }`}
            style={{
              transitionDelay: `${(index + 4) * 40}ms`,
            }}
          >
            <QuickActionCard
              title={action.title}
              description={action.description}
              icon={action.icon}
              onClick={() => navigate(action.path)}
            />
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div
        className={`grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 transition-opacity duration-300 ${animateCards ? "opacity-100" : "opacity-0"
          }`}
        style={{ transitionDelay: "300ms" }}
      >
        {/* Left Column - Recent Activity */}
        <div className="space-y-4 lg:col-span-2 lg:space-y-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-xl border transition-all duration-300 shadow-soft border-neutral-200 hover:shadow-lg">
            <div className="px-4 py-3 border-b border-neutral-200">
              <h2 className="text-lg font-semibold text-neutral-900">
                {t("dashboard.recentActivity.title")}
              </h2>
            </div>
            <div className="p-4">
              {activityLoading ? (
                <div className="py-4">
                  <p className="text-sm text-neutral-600">
                    {t("dashboard.recentActivity.loading")}
                  </p>
                </div>
              ) : activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.map((activity, index) => (
                    <div
                      key={index}
                      className={`transition-opacity duration-300 ease-out ${animateCards ? "opacity-100" : "opacity-0"
                        }`}
                      style={{
                        transitionDelay: `${index * 40}ms`,
                      }}
                    >
                      <ActivityCard
                        title={activity.title}
                        description={activity.description}
                        time={activity.time}
                        icon={activity.icon}
                        iconColor={activity.iconColor}
                        iconBgColor={activity.iconBgColor}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-4">
                  <p className="text-sm text-neutral-600">
                    {t("dashboard.recentActivity.noActivity")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar Content */}
        <div className="space-y-4 lg:space-y-6">
          {/* Currently Working Doctors */}
          <div
            className={`transition-opacity duration-300 ease-out ${animateCards ? "opacity-100" : "opacity-0"
              }`}
          >
            {workingDoctorsLoading ? (
              <div className="p-4 bg-white rounded-xl border border-neutral-200">
                <p className="text-sm text-neutral-600">
                  {t("dashboard.workingDoctors.loading")}
                </p>
              </div>
            ) : workingDoctors.length > 0 ? (
              <DoctorList doctors={workingDoctors as Doctor[]} />
            ) : (
              <div className="p-4 bg-white rounded-xl border border-neutral-200">
                <p className="text-sm text-neutral-600">
                  {t("dashboard.workingDoctors.none")}
                </p>
              </div>
            )}
          </div>

          {/* Today's Schedule */}
          <div className="p-4 bg-white rounded-xl border transition-all duration-300 shadow-soft border-neutral-200 hover:shadow-lg">
            <h3 className="mb-3 text-base font-semibold text-neutral-900">
              {t("dashboard.todaysSchedule.title")}
            </h3>
            {scheduleLoading ? (
              <div className="py-4">
                <p className="text-sm text-neutral-600">
                  {t("dashboard.todaysSchedule.loading")}
                </p>
              </div>
            ) : todaySchedule.length > 0 ? (
              <div className="space-y-3">
                {todaySchedule.map((item) => (
                  <div
                    key={item.id}
                    className={`flex justify-between items-center p-3 rounded-lg transition-colors duration-200 ${item.isActive
                        ? "bg-ews-primary-50 hover:bg-ews-primary-100"
                        : "bg-neutral-50 hover:bg-neutral-100"
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`flex justify-center items-center w-8 h-8 rounded-lg ${item.isActive ? "bg-primary-500" : "bg-secondary-500"
                          }`}
                      >
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {item.title}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {item.time}
                          {item.endTime ? ` - ${item.endTime}` : ""}
                        </p>
                      </div>
                    </div>
                    {item.isActive && (
                      <div className="w-2 h-2 rounded-full animate-pulse bg-primary-500"></div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-4">
                <p className="text-sm text-neutral-600">
                  {t("dashboard.todaysSchedule.none")}
                </p>
              </div>
            )}
          </div>

          {/* Alerts */}
          <div className="p-4 bg-white rounded-xl border transition-all duration-300 shadow-soft border-neutral-200 hover:shadow-lg">
            <h3 className="mb-3 text-base font-semibold text-neutral-900">
              {t("dashboard.alerts.title")}
            </h3>
            {alertsLoading ? (
              <div className="py-4">
                <p className="text-sm text-neutral-600">
                  {t("dashboard.alerts.loading")}
                </p>
              </div>
            ) : alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert) => {
                  const bgColorClass =
                    alert.type === "warning"
                      ? "bg-warning-50 hover:bg-warning-100"
                      : alert.type === "error"
                        ? "bg-error-50 hover:bg-error-100"
                        : alert.type === "success"
                          ? "bg-success-50 hover:bg-success-100"
                          : "bg-info-50 hover:bg-info-100";

                  const iconColorClass =
                    alert.type === "warning"
                      ? "text-warning-600"
                      : alert.type === "error"
                        ? "text-error-600"
                        : alert.type === "success"
                          ? "text-success-600"
                          : "text-info-600";

                  const IconComponent =
                    alert.type === "success" ? CheckCircle : AlertCircle;

                  // Translate alert title and description
                  const translatedAlert = translateAlert(alert, t);

                  return (
                    <div
                      key={alert.id}
                      className={`flex items-start p-3 space-x-3 rounded-lg transition-colors duration-200 ${bgColorClass}`}
                    >
                      <IconComponent
                        className={`w-5 h-5 ${iconColorClass} mt-0.5 ${alert.severity === "high" ? "animate-pulse" : ""
                          }`}
                      />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {translatedAlert.title}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {translatedAlert.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-4">
                <p className="text-sm text-neutral-600">
                  {t("dashboard.alerts.none")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
